import { toPercent } from "src/util/currency-formatter";

export const calculateStageTotals = (stages) => {
    const stageTotal = {};
    const taskTotals = {};
    const defaultSectionTotals = {
        materialCost: 0.00,
        laborCost: 0.00,
        subcontractorCost: 0.00,
        materialSale: 0.00,
        laborSale: 0.00,
        subcontractorSale: 0.00,
        total: 0.00,
        subTotal: 0.00,
        materialProfit: 0.00,
        laborProfit: 0.00,
        subcontractorProfit: 0.00,
        totalProfit: 0.00,
        materialProfitPercentage: 0.00,
        laborProfitPercentage: 0.00,
        subcontractorProfitPercentage: 0.00,
        totalProfitPercentage: 0.00,
        percentageComplete: 0.00,
        taskCount: 0,
    };

    // Helper function to initialize totals
    const initializeTotals = (target, id, parentId = null) => {
        if (!target[id]) {
            target[id] = {
                ...defaultSectionTotals,
                parentStageId: parentId,
                isNested: parentId !== null
            };
        }
    }

    // Helper function to process tasks within stages
    const processTasks = (tasks, stageId) => {
        initializeTotals(stageTotal, stageId);

        tasks.forEach(task => {
            const taskId = task.id;
            initializeTotals(taskTotals, taskId);

            const materialCost = task.materialTotal_Cost || 0.00;
            const laborCost = task.laborTotal_Cost || 0.00;
            const subcontractorCost = task.subcontractorTotal_Cost || 0.00;
            const materialSale = task.materialTotal_SaleAmount || 0.00;
            const laborSale = task.laborTotal_SaleAmount || 0.00;
            const subcontractorSale = task.subcontractorTotal_SaleAmount || 0.00;
            const materialProfit = task.material_Profit || 0.00;
            const laborProfit = task.labor_Profit || 0.00;
            const subcontractorProfit = task.subcontractor_Profit || 0.00;

            // Update task totals
            taskTotals[taskId].materialCost = materialCost;
            taskTotals[taskId].laborCost = laborCost;
            taskTotals[taskId].subcontractorCost = subcontractorCost;
            taskTotals[taskId].materialSale = materialSale;
            taskTotals[taskId].laborSale = laborSale;
            taskTotals[taskId].subcontractorSale = subcontractorSale;
            taskTotals[taskId].total = materialSale + laborSale + subcontractorSale;
            taskTotals[taskId].subTotal = materialCost + laborCost + subcontractorCost;
            taskTotals[taskId].materialProfit = task.material_Profit || 0.00;
            taskTotals[taskId].laborProfit = task.labor_Profit || 0.00;
            taskTotals[taskId].subcontractorProfit = task.subcontractor_Profit || 0.00;
            taskTotals[taskId].totalProfit = task.totalProfit || (materialProfit + laborProfit + subcontractorProfit) || 0.00;
            taskTotals[taskId].percentageComplete = task.percentageComplete || 0.00;
            taskTotals[taskId].taskCount = 1; // Each task counts as 1

            // Update stage totals
            stageTotal[stageId].materialCost += materialCost;
            stageTotal[stageId].laborCost += laborCost;
            stageTotal[stageId].subcontractorCost += subcontractorCost;
            stageTotal[stageId].materialSale += materialSale;
            stageTotal[stageId].laborSale += laborSale;
            stageTotal[stageId].subcontractorSale += subcontractorSale;
            stageTotal[stageId].total += materialSale + laborSale + subcontractorSale;
            stageTotal[stageId].subTotal += materialCost + laborCost + subcontractorCost;
            stageTotal[stageId].materialProfit += task.material_Profit || 0.00;
            stageTotal[stageId].laborProfit += task.labor_Profit || 0.00;
            stageTotal[stageId].subcontractorProfit += task.subcontractor_Profit || 0.00;
            stageTotal[stageId].totalProfit += task.totalProfit || (materialProfit + laborProfit + subcontractorProfit) || 0.00;
            stageTotal[stageId].percentageComplete += task.percentageComplete || 0.00;
            stageTotal[stageId].taskCount += 1;
        });
    };

    // Helper function to roll up totals to parent
    const rollUpToParent = (parentId, childTotals) => {
        initializeTotals(stageTotal, parentId, null);

        stageTotal[parentId].materialCost += childTotals.materialCost;
        stageTotal[parentId].laborCost += childTotals.laborCost;
        stageTotal[parentId].subcontractorCost += childTotals.subcontractorCost;
        stageTotal[parentId].materialSale += childTotals.materialSale;
        stageTotal[parentId].laborSale += childTotals.laborSale;
        stageTotal[parentId].subcontractorSale += childTotals.subcontractorSale;
        stageTotal[parentId].total += childTotals.total;
        stageTotal[parentId].subTotal += childTotals.subTotal;
        stageTotal[parentId].materialProfit += childTotals.materialProfit;
        stageTotal[parentId].laborProfit += childTotals.laborProfit;
        stageTotal[parentId].subcontractorProfit += childTotals.subcontractorProfit;
        stageTotal[parentId].totalProfit += childTotals.totalProfit;
        stageTotal[parentId].percentageComplete += childTotals.percentageComplete;
        stageTotal[parentId].taskCount += childTotals.taskCount;
    };

    // Traverse stages and process their tasks and sub-stages
    const traverseStages = (stages, parentId = null) => {
        if (!stages || !Array.isArray(stages)) return;

        stages.forEach(stage => {
            const currentStageId = stage.id;
            initializeTotals(stageTotal, currentStageId, parentId);

            // Process tasks in the current stage
            if (Array.isArray(stage.tasks) && stage.tasks.length > 0) {
                processTasks(stage.tasks, currentStageId);
            }

            // Process sub-stages recursively
            if (Array.isArray(stage.stages) && stage.stages.length > 0) {
                traverseStages(stage.stages, currentStageId);
            }

            // Roll up totals to parent after processing all children
            if (parentId) {
                rollUpToParent(parentId, stageTotal[currentStageId]);
            }
        });
    };

    // Start traversal from top-level stages
    traverseStages(stages);

    // Finalize calculations for stages
    Object.keys(stageTotal).forEach(stageId => {
        const summary = stageTotal[stageId];
        if (summary.taskCount > 0) {
            summary.materialProfitPercentage = toPercent(summary.materialProfit, summary.materialCost);
            summary.laborProfitPercentage = toPercent(summary.laborProfit, summary.laborCost);
            summary.subcontractorProfitPercentage = toPercent(summary.subcontractorProfit, summary.subcontractorCost);
            summary.totalProfitPercentage = toPercent(summary.totalProfit, summary.subTotal);
            summary.completionPercentage = summary.percentageComplete / summary.taskCount;
        }
        // Assign final values
        summary.materialTotal_Cost = summary.materialCost;
        summary.laborTotal_Cost = summary.laborCost;
        summary.subcontractorTotal_Cost = summary.subcontractorCost;
        summary.materialTotal_SaleAmount = summary.materialSale;
        summary.laborTotal_SaleAmount = summary.laborSale;
        summary.subcontractorTotal_SaleAmount = summary.subcontractorSale;
    });

    // Finalize calculations for tasks
    Object.keys(taskTotals).forEach(taskId => {
        const task = taskTotals[taskId];
        task.materialProfitPercentage = toPercent(task.materialProfit, task.materialCost);
        task.laborProfitPercentage = toPercent(task.laborProfit, task.laborCost);
        task.subcontractorProfitPercentage = toPercent(task.subcontractorProfit, task.subcontractorCost);
        task.totalProfitPercentage = toPercent(task.totalProfit, task.subTotal);
        task.completionPercentage = task.percentageComplete;
        // Assign final values
        task.materialTotal_Cost = task.materialCost;
        task.laborTotal_Cost = task.laborCost;
        task.subcontractorTotal_Cost = task.subcontractorCost;
        task.materialTotal_SaleAmount = task.materialSale;
        task.laborTotal_SaleAmount = task.laborSale;
        task.subcontractorTotal_SaleAmount = task.subcontractorSale;
    });

    // Return both stage and task totals
    return {
        stageSummary: stageTotal,
        taskSummary: taskTotals
    };
};
export const calculateVariationsBudget = (option, progressClaims = []) => {
    const materialSale = parseFloat(option.materialTotal_SaleAmount) || 0;
    const laborSale = parseFloat(option.laborTotal_SaleAmount) || 0;
    const subcontractorSale = parseFloat(option.subcontractorTotal_SaleAmount) || 0;
    const total = materialSale + laborSale + subcontractorSale;

    const materialCost = parseFloat(option.materialTotal_Cost) || 0;
    const laborCost = parseFloat(option.laborTotal_Cost) || 0;
    const subcontractorCost = parseFloat(option.subcontractorTotal_Cost) || 0;
    const subtotal = materialCost + laborCost + subcontractorCost;

    const materialProfit = parseFloat(option.material_Profit) || 0;
    const laborProfit = parseFloat(option.labor_Profit) || 0;
    const subcontractorProfit = parseFloat(option.subcontractor_Profit) || 0;
    const total_profit = materialProfit + laborProfit + subcontractorProfit;
    const taskTotalClaimAmount = progressClaims.reduce((sum, claim) => {
        const task = claim.progressClaimTask?.find(t => t.taskId === option.id);
        return sum + (task?.total || 0);
    }, 0);

    return {
        total,
        subtotal,
        total_profit,
        taskTotalClaimAmount,
        material: {
            sale: materialSale,
            cost: materialCost,
            profit: materialProfit,
        },
        labor: {
            sale: laborSale,
            cost: laborCost,
            profit: laborProfit,
        },
        subcontractor: {
            sale: subcontractorSale,
            cost: subcontractorCost,
            profit: subcontractorProfit,
        },
    };
};
export const calculatVariationsTotalsBudget = (options = [], progressClaims = []) => {
    return options.reduce((acc, option) => {
        const taskTotal = calculateVariationsBudget(option,progressClaims);
        const actualCost =
            parseFloat(option.actualMaterialCost || 0) +
            parseFloat(option.actualLaborCost || 0) +
            parseFloat(option.actualSubContractorCost || 0);
        const committedCost = parseFloat(option.committedCost || 0);

        return {
            total: acc.total + taskTotal.total,
            subtotal: acc.subtotal + taskTotal.subtotal,
            committedCost: acc.committedCost + committedCost,
            budget: acc.budget + actualCost,
            total_profit: acc.total_profit + taskTotal.total_profit,
            claim_total: acc.claim_total + taskTotal.taskTotalClaimAmount, 
        };
    }, { total: 0, subtotal: 0, committedCost: 0, budget: 0, total_profit: 0, claim_total: 0 });
};



export const calculateOptionsTotals = (options, projectType) => {
    let filteredOptions = options;
    if (projectType === 'PROJECT') {
        filteredOptions = options.filter(option => option.proposal_status === 'APPROVED');
    }

    const stageTotal = {};
    const optionTotals = {};

    const defaultOptionTotals = {
        materialCost: 0.00,
        laborCost: 0.00,
        subcontractorCost: 0.00,
        materialSale: 0.00,
        laborSale: 0.00,
        subcontractorSale: 0.00,
        total: 0.00,
        subTotal: 0.00,
        materialProfit: 0.00,
        laborProfit: 0.00,
        subcontractorProfit: 0.00,
        totalProfit: 0.00,
        materialProfitPercentage: 0.00,
        laborProfitPercentage: 0.00,
        subcontractorProfitPercentage: 0.00,
        totalProfitPercentage: 0.00,
        percentageComplete: 0.00,
        taskCount: 0,
    };

    // Helper function to initialize totals
    const initializeTotals = (target, id, parentId = null) => {
        if (!target[id]) {
            target[id] = {
                ...defaultOptionTotals,
                parentStageId: parentId,
                isNested: parentId !== null
            };
        }
    };

    Array.isArray(filteredOptions) && filteredOptions.forEach(option => {
        const optionId = option.id;
        initializeTotals(optionTotals, optionId);

        const materialCost = option.materialTotal_Cost || 0.00;
        const laborCost = option.laborTotal_Cost || 0.00;
        const subcontractorCost = option.subcontractorTotal_Cost || 0.00;
        const materialSale = option.materialTotal_SaleAmount || 0.00;
        const laborSale = option.laborTotal_SaleAmount || 0.00;
        const subcontractorSale = option.subcontractorTotal_SaleAmount || 0.00;
        const materialProfit = option.material_Profit || 0.00;
        const laborProfit = option.labor_Profit || 0.00;
        const subcontractorProfit = option.subcontractor_Profit || 0.00;

        const stageId = option.stageId || 1; //if not sections assigned then assign to default section id 1


        // Update option totals
        optionTotals[optionId].materialCost = materialCost;
        optionTotals[optionId].laborCost = laborCost;
        optionTotals[optionId].subcontractorCost = subcontractorCost;
        optionTotals[optionId].materialSale = materialSale;
        optionTotals[optionId].laborSale = laborSale;
        optionTotals[optionId].subcontractorSale = subcontractorSale;
        optionTotals[optionId].total = materialSale + laborSale + subcontractorSale;
        optionTotals[optionId].subTotal = materialCost + laborCost + subcontractorCost;
        optionTotals[optionId].materialProfit = option.material_Profit || 0.00;
        optionTotals[optionId].laborProfit = option.labor_Profit || 0.00;
        optionTotals[optionId].subcontractorProfit = option.subcontractor_Profit || 0.00;
        optionTotals[optionId].totalProfit = option.totalProfit || (materialProfit + laborProfit + subcontractorProfit) || 0.00;
        optionTotals[optionId].percentageComplete = option.percentageComplete || 0.00;
        optionTotals[optionId].taskCount = 1;

        if (!stageTotal[stageId]) {
            initializeTotals(stageTotal, stageId);
        }
        // Update stage totals
        stageTotal[stageId].materialCost += materialCost;
        stageTotal[stageId].laborCost += laborCost;
        stageTotal[stageId].subcontractorCost += subcontractorCost;
        stageTotal[stageId].materialSale += materialSale;
        stageTotal[stageId].laborSale += laborSale;
        stageTotal[stageId].subcontractorSale += subcontractorSale;
        stageTotal[stageId].total += materialSale + laborSale + subcontractorSale;
        stageTotal[stageId].subTotal += materialCost + laborCost + subcontractorCost;
        stageTotal[stageId].materialProfit += option.material_Profit || 0.00;
        stageTotal[stageId].laborProfit += option.labor_Profit || 0.00;
        stageTotal[stageId].subcontractorProfit += option.subcontractor_Profit || 0.00;
        stageTotal[stageId].totalProfit += option.totalProfit || (materialProfit + laborProfit + subcontractorProfit) || 0.00;
        stageTotal[stageId].percentageComplete += option.percentageComplete || 0.00;
        stageTotal[stageId].taskCount += 1;
    });

    // Finalize calculations for stages
    Object.keys(stageTotal).forEach(stageId => {
        const summary = stageTotal[stageId];
        summary.materialProfitPercentage = toPercent(summary.materialProfit, summary.materialCost);
        summary.laborProfitPercentage = toPercent(summary.laborProfit, summary.laborCost);
        summary.subcontractorProfitPercentage = toPercent(summary.subcontractorProfit, summary.subcontractorCost);
        summary.totalProfitPercentage = toPercent(summary.totalProfit, summary.subTotal);
        summary.completionPercentage = summary.percentageComplete;
        // Assign final values
        summary.materialTotal_Cost = summary.materialCost;
        summary.laborTotal_Cost = summary.laborCost;
        summary.subcontractorTotal_Cost = summary.subcontractorCost;
        summary.materialTotal_SaleAmount = summary.materialSale;
        summary.laborTotal_SaleAmount = summary.laborSale;
        summary.subcontractorTotal_SaleAmount = summary.subcontractorSale;
    });

    // Finalize calculations for options
    Object.keys(optionTotals).forEach(optionId => {
        const summary = optionTotals[optionId];
        summary.materialProfitPercentage = toPercent(summary.materialProfit, summary.materialCost);
        summary.laborProfitPercentage = toPercent(summary.laborProfit, summary.laborCost);
        summary.subcontractorProfitPercentage = toPercent(summary.subcontractorProfit, summary.subcontractorCost);
        summary.totalProfitPercentage = toPercent(summary.totalProfit, summary.subTotal);
        summary.completionPercentage = summary.percentageComplete;
        // Assign final values
        summary.materialTotal_Cost = summary.materialCost;
        summary.laborTotal_Cost = summary.laborCost;
        summary.subcontractorTotal_Cost = summary.subcontractorCost;
        summary.materialTotal_SaleAmount = summary.materialSale;
        summary.laborTotal_SaleAmount = summary.laborSale;
        summary.subcontractorTotal_SaleAmount = summary.subcontractorSale;
    });

    // Return both stage and task totals
    return {
        stageSummary: stageTotal,
        optionSummary: optionTotals
    };
};


export const getTotal = (data) => {
    const total = {
        materialSaleAmount: 0.00,
        laborSaleAmount: 0.00,
        subcontractorSaleAmount: 0.00,
        materialCost: 0.00,
        laborCost: 0.00,
        subcontractorCost: 0.00,
        materialProfit: 0.00,
        laborProfit: 0.00,
        subcontractorProfit: 0.00,
        materialProfitPercentage: 0.00,
        laborProfitPercentage: 0.00,
        subcontractorProfitPercentage: 0.00,
        total: 0.00,
        totalCost: 0.00,
        totalProfit: 0.00,
        totalProfitPercentage: 0.00,
    };
    if (data) {
        Object.values(data).forEach((summary) => {
            if (summary.parentStageId === null) {
                total.materialSaleAmount += summary.materialSale || 0;
                total.laborSaleAmount += summary.laborSale || 0;
                total.subcontractorSaleAmount += summary.subcontractorSale || 0;
                total.materialCost += summary.materialCost || 0;
                total.laborCost += summary.laborTotal_Cost || 0;
                total.subcontractorCost += summary.subcontractorCost || 0;
                total.materialProfit += summary.materialProfit || 0;
                total.laborProfit += summary.laborProfit || 0;
                total.subcontractorProfit += summary.subcontractorProfit || 0;
                total.total += (summary.materialSale || 0) + (summary.laborSale || 0) + (summary.subcontractorSale || 0);
                total.totalProfit += summary.totalProfit || 0;
                total.totalCost += (summary.materialCost || 0) + (summary.laborTotal_Cost || 0) + (summary.subcontractorCost || 0);
            }
        });
        total.materialProfitPercentage = total.materialCost ? ((total.materialProfit / total.materialCost) * 100) : 0.00;
        total.laborProfitPercentage = total.laborCost ? ((total.laborProfit / total.laborCost) * 100) : 0.00;
        total.subcontractorProfitPercentage = total.subcontractorCost ? ((total.subcontractorProfit / total.subcontractorCost) * 100) : 0.00;
        total.totalProfitPercentage = total.total ? ((total.totalProfit / total.totalCost) * 100) : 0.00;
    }
    return total;
};

export const findStageData = (data, targetStageId) => {
    for (const item of data) {
        // Check if the current item is the stage you're looking for
        if (item.id === targetStageId) return item;

        // Check if the current item has stages and recursively search within them
        if (item.stages && Array.isArray(item.stages)) {
            const foundStage = findStageData(item.stages, targetStageId);
            if (foundStage) return foundStage;

            //check if any stage in the array has parent_stage_id equal to targetStageId
            const childStage = item.stages.find(stage => stage.parent_stage_id === targetStageId);
            if (childStage) return childStage;
        }

        // Check if the current item has tasks and if any task has stageId
        if (item.tasks && Array.isArray(item.tasks)) {
            for (const task of item.tasks) {
                if (task.stageId === targetStageId) return task;
            }
        }
    }

    return null; // Return null if nothing is found
};